<template>
  <section class="hero d-flex align-items-center">
    <div class="hero-wrapper">
      <div class="container px-lg-0 py-lg-5 px-3">
        <div class="row h-100 align-items-center mt-4">
          <div class="col-lg-5 hero-text">
            <h1 class="display-2 fw-bold mb-3">
              Las mejores <br />
              <span class="gradient_title">semillas</span> para <br class="d-md-block d-none" />
              tu cultivo<span style="color: var(--primary-color)">.</span>
            </h1>
            <p class="lead mb-4 col-lg-10 col-11 fs-6">
              Explorá entre nuestras variedades de semillas y
              descubrí las mejores genéticas para tu cultivo en el mejor lugar.
            </p>
            <div class="d-lg-flex d-block gap-3">
              <a
                class="btn btn-primary me-2 fs-lg-6 btn-lg py-2 px-4 mt-3"
                href="/productos"
              >
                <i class="fas fa-store me-2"></i>
                Ver catálogo
              </a>
              <router-link
                to="/como-germinar"
                class="btn btn-outline-light fs-lg-5 btn-lg py-2 px-4 mt-3"
              >
                <i class="fa-solid fa-book me-2"></i>
                Garantía y Germinación
              </router-link>
            </div>
          </div>
          <div class="col-lg-7 mt-lg-0 mt-5 pt-lg-0 pt-5 hero-images-container">
            <div class="hero-carousel-wrapper">
              <div class="hero-carousel">
                <div class="carousel-container">
                  <a v-for="(group, index) in imageGroups" 
                     :key="index" 
                     :href="group.searchLink" 
                     class="carousel-slide"
                     :class="{ 
                       'active': index === currentSlide,
                       'prev': index === getPrevSlide,
                       'next': index === getNextSlide
                     }"
                  >
                    <img 
                      :src="group.image" 
                      alt="Semillas" 
                      class="hero-image"
                    />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
  
  <!-- Brand Logos Section -->
  <section class="brands-section container-fluid d-lg-block d-none mb-0">
    <div class="container-fluid">
      <div class="brands-wrapper" 
           @mouseenter="pauseBrandsCarousel" 
           @mouseleave="resumeBrandsCarousel">
        <div class="brands-track" v-if="!brandsLoading" :class="{ 'paused': isPaused }">
          <div class="brands-grid">
            <div v-for="brand in filteredBrands" :key="brand.brand_id" class="brand-item">
              <router-link :to="{ path: '/productos', query: { bank: slugify(brand.brand_name) }}" class="brand-link">
                <img :src="brand.image" :alt="brand.brand_name" />
              </router-link>
            </div>
          </div>
          <!-- Duplicate for seamless scroll -->
          <div class="brands-grid ps-0">
            <div v-for="brand in filteredBrands" :key="brand.brand_id + '-duplicate'" class="brand-item">
              <router-link :to="{ path: '/productos', query: { bank: slugify(brand.brand_name) }}" class="brand-link">
                <img :src="brand.image" :alt="brand.brand_name" />
              </router-link>
            </div>
          </div>
        </div>
        <!-- Skeleton loader for brands -->
        <div class="brands-track" v-else>
          <div class="brands-grid">
            <div v-for="n in 5" :key="'skeleton-brand-' + n" class="brand-item">
              <div class="skeleton-brand"></div>
            </div>
          </div>
          <div class="brands-grid ps-0">
            <div v-for="n in 5" :key="'skeleton-brand-dup-' + n" class="brand-item">
              <div class="skeleton-brand"></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>

  <section class="catalog px-4 pt-5 d-flex align-items-center">
    <div class="container pt-5">
      <div class="catalog__header mb-3">
        <h2 class="catalog__title display-4 text-white mb-3 fw-semibold">
          Últimos ingresos<span style="color: var(--primary-color)">.</span>
        </h2>
        <p class="catalog__subtitle text-gray col-lg-7">
          Descubre las últimas variedades de semillas que ingresaron a nuestro
          catálogo.
        </p>
      </div>
      <div class="d-flex justify-content-start mb-4">
        <a
          class="btn btn-primary me-2 fs-lg-6 btn-lg py-2 px-4"
          href="/productos"
        >
          <i class="fas fa-store me-2"></i>
          Ver todas
        </a>
      </div>
      <div class="catalog__products">
        <ProductsCarousel :products="products" :isLoading="productsLoading" carouselType="latest" class="mt-0"/>
      </div>
    </div>
  </section>

  <!-- Genetics Section -->
  <section class="genetics container-fluid d-flex justify-content-center px-4">
    <div class="container">
      <div class="genetics__header mb-5">
        <h2 class="genetics__title display-3 mb-3 fw-semibold">
          Las mejores genéticas<span style="color: var(--primary-color)"
            >.</span
          >
        </h2>
        <p class="genetics__subtitle text-gray fs-6">
          Elegí entre las mejores genéticas del mercado nacional e internacional
          en un solo lugar.
        </p>
      </div>

      <div class="row gy-4 mb-5">
        <div class="col-lg-6">
          <a
            href="/productos?category=automaticas"
            class="text-decoration-none card genetics-card h-100"
          >
            <div class="card-body p-3">
              <div class="card-image-wrapper feminized">
                <img
                  src="@/assets/images/glueauto.png"
                  alt="Gorilla glue flower"
                  class="card-image feminized-image"
                />
              </div>
              <h3 class="card-title fw-bold mb-0 d-flex align-items-center justify-content-end">
                Semillas <br />
                Automáticas
              </h3>
            </div>
          </a>
        </div>
        <div class="col-lg-6 col-12 pe-lg-4">
          <a
            href="/productos?category=feminiizadas"
            class="text-decoration-none card genetics-card h-100"
          >
          <div class="card-image-wrapper seeds_box">
            <img
              src="@/assets/images/seeds_box_to_left.png"
              alt="cannabis flower"
              class="card-image seeds_box-image"
            />
          </div>
            <div class="card-body p-3 d-flex align-items-start">
              <h3 class="card-title fw-bold mb-0">
                Semillas <br />
                feminizadas
              </h3>
            </div>
          </a>
        </div>
        <div class="col-lg-5 col-12">
            <a
            href="/productos?category=regulares"
            class="text-decoration-none card genetics-card h-100"
            >
            <div class="card-body p-4 ps-0 position-relative h-100">
              <div class="position-absolute top-0 end-0 p-3">
              <h3 class="fw-bold mb-0 card-title text-end">
                Semillas <br />
                Regulares
              </h3>
              </div>
              <div class="position-absolute bottom-0 start-0">
              <img
                src="@/assets/images/cannabis-leaf.png"
                alt="cannabis flower"
                class="img-fluid col-lg-8 col-6"
              />
              </div>
            </div>
            </a>
        </div>
        <div class="col-lg-7 col-12 pe-lg-4">
          <a
            href="/como-germinar"
            class="text-decoration-none card genetics-card h-100"
            style="overflow: hidden;"
          >
            <div class="card-body px-lg-4" style="z-index: 2;">
              <h3 class="fw-bold card-title mb-3">
                Garantía de germinación
              </h3>
              <p class="text-muted small mb-0 col-lg-8 col-7">
                Nuestras semillas cuentan con garantía de germinación por parte
                del banco.
              </p>
            </div>
            <div class="position-absolute verified-icon">
              <img src="@/assets/icons/verified.svg" alt="check icon" class="img-fluid col-12 opacity-50">
            </div>
          </a>
        </div>
      </div>
    </div>
  </section>

  <section class="catalog px-4 d-flex align-items-center" v-if="isLoading || productsOnSale.length > 0">
    <div class="container">
      <div class="catalog__header mb-3 text-center">
        <h2 class="catalog__title display-4 text-white mb-0 fw-semibold">
          Ofertas<span style="color: var(--primary-color)">.</span>
        </h2>
        <div class="d-flex justify-content-center">
          <p class="catalog__subtitle text-gray col-lg-7">
            Descubre las últimas ofertas en semillas que tenemos para vos.
          </p>
        </div>
      </div>
      <div class="catalog__products">
        <ProductsCarousel :products="productsOnSale" :isLoading="isLoading" carouselType="offers" class="mt-0"/>
      </div>
    </div>
  </section>

  <!-- FAQ Section -->
  <section class="faq-section container-fluid px-3" id="preguntas-frecuentes">
    <div class="d-flex justify-content-center mt-5 px-0">

      <!-- Content Container -->
      <div class="container position-relative row d-flex justify-content-center">
        <div class="col-5 d-lg-flex d-none align-items-start">
          <img src="@/assets/images/tree.png" alt="" class="img-fluid col-3 floating-image floating-image-blurred">
          <img src="@/assets/images/tree.png" alt="" class="img-fluid col-7 floating-image">
        </div>
        <div class="col-lg-6">

          <div class="faq-header mb-5">
            <h2 class="display-5 text-white fw-semibold mb-3">
              Preguntas Frecuentes<span class="text-primary">.</span>
            </h2>
            <p class="text-gray">
              Encuentra respuestas rápidas a las preguntas más comunes sobre
              nuestros productos y servicios.
            </p>
          </div>

          <div class="accordion custom-accordion" id="faqAccordion">
            <div
              class="accordion-item mb-3"
              v-for="(faq, index) in faqs"
              :key="index"
            >
              <h2 class="accordion-header">
                <button
                  class="accordion-button collapsed"
                  type="button"
                  data-bs-toggle="collapse"
                  :data-bs-target="'#faq' + index"
                >
                  <i :class="faq.icon" class="me-3 text-white"></i>
                  {{ faq.question }}
                </button>
              </h2>
              <div
                :id="'faq' + index"
                class="accordion-collapse collapse"
                data-bs-parent="#faqAccordion"
              >
                <div class="accordion-body">
                  {{ faq.answer }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
  <div class="loader-container" v-if="loadingPage">
    <div class="loader"></div>
  </div>
</template>

<script>
import ProductsCarousel from "@/components/ProductsCarousel.vue";
import { getProductsOnSale, getLatestProductsWithStock } from "@/services/products";
import { getBrands } from "@/services/brands";

export default {
  name: "PageHome",
  components: {
    ProductsCarousel,
  },
  data() {
    return {
      products: [],
      productsOnSale: [],
      loadingPage: true,
      productsLoading: true,
      isLoading: true,
      brandsLoading: true, // Add loading state for brands
      currentSlide: 0,
      imageGroups: [
        {
          searchLink: '/productos?search=royal queen',
          image: 
            require('@/assets/images/royal-queen-blisters.png'),
        },
        {
            searchLink: '/productos?search=se%2B',
          image: 
            require('@/assets/images/semas_blisters.png'),
        },
        {
          searchLink: '/productos?search=gorilla bsf',
          image: 
            require('@/assets/images/bsf-blisters.png'),
        },
        {
          searchLink: '/productos?search=sweed lab',
          image: 
            require('@/assets/images/sweedlab.png'),
        },
      ],
      faqs: [
        {
          icon: "fas fa-shipping-fast",
          question: "¿Realizan envíos a todo el país?",
          answer:
            "Sí, realizamos envíos a todo el país a través de diferentes servicios de mensajería.",
        },
        {
          icon: "fas fa-seedling",
          question: "¿Las semillas tienen garantía?",
          answer:
            "Sí, todas nuestras semillas cuentan con garantía de germinación.",
        },
        {
          icon: "fas fa-credit-card",
          question: "¿Qué métodos de pago aceptan?",
          answer:
            "Al confirmar el pedido se te indicará como proceder con el pago através de Whatsapp.",
        },
      ],
      brandsList: [], // Nuevo array para los bancos desde la API
      isPaused: false,
    };
  },
  computed: {
    getPrevSlide() {
      return (this.currentSlide - 1 + this.imageGroups.length) % this.imageGroups.length;
    },
    getNextSlide() {
      return (this.currentSlide + 1) % this.imageGroups.length;
    },
    filteredBrands() {
      return this.brandsList.filter(brand => 
        brand.image !== 'https://res.cloudinary.com/dkpmp8fwq/image/upload/v1748839616/logo.c7d24e78_rdvx4o.png'
      );
    }
  },
  methods: {
    // Add new slugify method to format brand names for URLs
    slugify(text) {
      return text
        ?.toString()
        .toLowerCase()
        .trim()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/[\s-]+/g, '-');
    },
    async loadProducts() {
      try {
        this.productsLoading = true;
        // Usar la nueva función que obtiene productos más recientes con stock y precio
        const response = await getLatestProductsWithStock(8);
        // getLatestProductsWithStock devuelve un objeto con results
        const data = response.results || response;
        this.products = data;
        console.log('Productos más recientes con stock cargados en Home:', this.products);
      } catch (error) {
        console.error('Error loading products:', error);
      } finally {
        this.productsLoading = false;
      }
    },
    async loadProductsOnSale() {
      try {
        this.isLoading = true;
        const response = await getProductsOnSale();
        // Acceder a results que contiene el array de productos
        this.productsOnSale = response.results || [];
      } catch (error) {
        console.error('Error loading products on sale:', error);
        this.productsOnSale = []; // Asignar array vacío en caso de error
      } finally {
        this.isLoading = false;
      }
    },
    async loadBrands() {
      try {
        this.brandsLoading = true;
        const brands = await getBrands();
        this.brandsList = brands;
      } catch (error) {
        console.error('Error loading brands:', error);
        this.brandsList = []; // Array vacío en caso de error
      } finally {
        this.brandsLoading = false;
      }
    },
    goToSlide(index) {
      this.currentSlide = index;
    },
    nextSlide() {
      this.currentSlide = this.getNextSlide;
    },
    startAutoSlide() {
      setInterval(this.nextSlide, 5000); // Cambiar slide cada 5 segundos
    },
    pauseBrandsCarousel() {
      this.isPaused = true;
    },
    resumeBrandsCarousel() {
      this.isPaused = false;
    },
  },
  async mounted() {
    setTimeout(() => {
      this.loadingPage = false;
    }, 500);
    await Promise.all([
      this.loadProducts(),
      this.loadBrands(), // Cargar los bancos junto con los otros datos
    ]);
    this.startAutoSlide();
    await this.loadProductsOnSale();
  },
};
</script>

<style scoped>
.loader-container {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.9);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
}

.loader {
  width: 50px;
  height: 50px;
  border: 3px solid var(--primary-color);
  border-radius: 50%;
  border-top-color: transparent;
  animation: spin 1s linear infinite;
  margin: 0 auto;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
.hero {
  min-height: 100vh;
  position: relative;
  background-color: var(--black-color);
  overflow: hidden; /* Cambiado a hidden */
}

.hero-wrapper {
  width: 100%;
  padding: 50px 0;
  position: relative;
}

.hero-carousel-wrapper {
  position: relative;
  width: 100%;
  overflow: visible;
  padding: 2rem;
  margin: -2rem; /* Compensar el padding para mantener alineación */
}

.hero-text {
  position: relative;
  z-index: 3; /* Mayor z-index para estar por encima de ambos gradientes */
  animation: fadeIn 1s ease;
}

.hero-content {
  position: relative;
  z-index: 3;
}

.catalog{
  background-color: var(--black-color) !important;
  padding: 0 0 3rem 0;
  min-height: 60vh;
  position: relative;
}

.gradient_title {
  background: linear-gradient(
    90deg,
    var(--primary-color),
    var(--tertiary-color)
  );
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
}
.hero-image {
  display: flex;
  filter: drop-shadow(20px 0px 3rem rgba(114, 0, 137, 0.6));
  transition: all 0.3s ease;
  max-height: 400px !important;
}

.hero-image:hover{
    cursor: pointer;
    transform: scale(1.03);
    transition: .4s all  ease;
}

.brands-section::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 1px;
  background: linear-gradient(to right, transparent, var(--primary-color), transparent);
}

.genetics {
  background-color: var(--black-color);
  padding: 3rem 0;
  min-height: 60vh;
  position: relative;
}

.genetics-card {
  border-radius: 16px;
  border: none;
  height: 280px;
  overflow: visible;
  transition: all 0.3s ease;
  cursor: pointer;
  position: relative;
  background-color: #fff;
}
.genetics img {
  opacity: 1;
}
.genetics-card:hover {
  transform: translateY(-5px);
}
.genetics-card:hover {
  transform: translateY(-5px);
}
.card {
  min-height: 220px;
}

.card-title {
  color: var(--black-color-3);
  position: relative;
  z-index: 2;
  font-size: 2.5rem;
}

.card-image-wrapper {
  position: absolute;
  overflow: visible;
}

.card-image-wrapper.feminized {
  bottom: 12%;
  left: 5%;
  width: 180px;
}

.card-image-wrapper.seeds_box {
  top: -20%;
  right: 0;
  width: 300px;
  height: 300px;
}
.verified-icon{
  width: 300px; bottom: -100px; right: -80px; z-index: 1;
}

.card-image {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.feminized-image {
  transform: scale(1.2);
}
.seeds_box-image {
  width: 100%;
}

.heart-icon i {
  color: white;
  font-size: 1.5rem;
}
.floating-image{
  animation: floating 3s infinite;
  filter: drop-shadow(0px 50px 100px rgba(61, 61, 61, 0.6));
}
.floating-image-blurred{
  filter: blur(4px);
  opacity: 0.5;
}
@keyframes floating {
  0% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-20px);
  } 
  100% {
    transform: translateY(0);
  }
}

@media (max-width: 991.98px) {
  .card-image-wrapper.feminized {
    bottom: 13%;
    right: 0;
    width: 200px;
  }
  .hero-wrapper {
    padding-top: 140px;
    padding-bottom: 30px;
  }

  .hero-carousel-wrapper {
    padding: 1rem;
    margin: -1rem;
  }

  .hero-image {
    width: 100%;
    max-width: 100%;
    height: auto;
  }
  .hero-images{
    
  overflow-x: scroll;
  }
  .card-image-wrapper.seeds_box {
    top: auto;
    bottom: -10%;
    right: 0;
    width: 285px;
    height: 285px;
  }
  .hero-image {
    right: -400px;
    top: 350px;
  }
}

.faq-section {
  background: var(--black-color);
  position: relative;
  overflow: hidden;
  min-height: 0;
}

.accordion{
  width: 100%;
  min-height: 400px;
}

.flower-image {
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 40%;
  z-index: 1;
}

.flower-image img {
  width: 100%;
  height: auto;
  object-fit: contain;
}

.container {
  z-index: 2;
}

.custom-accordion .accordion-item {
  background: var(--black-color-3);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  overflow: hidden;
}

.custom-accordion .accordion-button {
  background: var(--black-color-3);
  color: var(--white-color);
  border: none;
  padding: 1.25rem;
  font-weight: 500;
}

.custom-accordion .accordion-button:not(.collapsed) {
  background: var(--primary-color);
  color: white;
}

.custom-accordion .accordion-button::after {
  filter: invert(1);
}

.custom-accordion .accordion-body {
  background: var(--black-color-2);
  color: var(--white-color-2);
  padding: 1.25rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.accordion-button:hover {
  background: rgba(255, 255, 255, 0.05);
}

.accordion-button:focus {
  box-shadow: none;
  border-color: var(--primary-color);
}

@media (max-width: 991.98px) {
  .card-title{
    font-size: 2.5rem;
  }
  .flower-image {
    width: 60%;
    opacity: 0.2;
  }
  .faq-section {
    min-height: 0;
  }
}
@media (max-width: 500px) {
  .card-title{
    font-size: 2rem;
  }
  .card-image-wrapper.feminized {
  bottom: 12%;
  right: -5%;
  width: 170px;
}

.card-image-wrapper.seeds_box {
  bottom: -10%;
  right: 0;
  width: 260px;
  height: 260px;
}
.card-image-wrapper.leaf {
  width: 80%;
}
.verified-icon{
  width: 200px; bottom: -50px; right: -50px; z-index: 1;
}
}

@media (max-width: 380px) {
  .card-title{
    font-size: 1.5rem !important;
  }
  .card-image-wrapper.feminized {
  bottom: 12%;
  right: 0% ;
  width: 170px;
}
.card-image-wrapper.seeds_box {
  bottom: -10%;
  right: 0;
  width: 240px;
  height: 240px;
}
.verified-icon{
  width: 150px; bottom: -10px; right: -40px; z-index: 1;
}
}

.hero-carousel {
  position: relative;
  width: 100%;
  overflow: visible;
  padding: 2rem 0;
}

.carousel-container {
  position: relative;
  height: 450px;
  width: 100%;
}

.carousel-slide {
  position: absolute;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  opacity: 0;
  transition: all 0.6s cubic-bezier(0.4, 0, 0.2, 1);
  transform: scale(0.8);
  pointer-events: none;
}

.carousel-slide.active {
  opacity: 1;
  transform: scale(1);
  pointer-events: auto;
}

.carousel-slide.prev {
  transform: translateX(-100%) scale(0.8);
  opacity: 0;
}

.carousel-slide.next {
  transform: translateX(100%) scale(0.8);
  opacity: 0;
}

.hero-image {
  max-height: 450px;
  filter: drop-shadow(0 0px 2rem rgba(0, 137, 16, 0));
  transition: all 0.6s cubic-bezier(0.4, 0, 0.2, 1);
}

.carousel-slide.active .hero-image {
  filter: drop-shadow(20px 0px 10rem rgba(110, 0, 137, 0.185));
}

.carousel-indicators {
  position: absolute;
  bottom: -1rem;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 0.5rem;
  z-index: 2;
}

.carousel-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.3);
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
  padding: 0;
}

.carousel-dot.active {
  background: var(--primary-color);
  transform: scale(1.2);
}

@media (max-width: 991.98px) {
  .carousel-container {
    height: 300px;
  }
  
  .hero-image {
    width: 100%;
  }
}

/* Actualizar los estilos de la sección de brands */
.brands-section {
  background: var(--black-color);
  padding: 3rem 0;
  border-top: 1px solid rgba(255, 255, 255, 0.05);
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 0;
  overflow: hidden;
  min-height: 0;
}

.brands-grid {
  display: flex;
  gap: 2rem;
  padding: 0 4rem;
  align-items: center;
}

.brand-item {
  flex: 0 0 auto;
  width: 220px;
  height: 130px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.brand-item img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  filter: grayscale(100%) brightness(200%);
  opacity: 0.5;
  transition: all 0.3s ease;
  padding: 1rem;
  max-width: 200px; /* Asegurar que los logos no sean demasiado grandes */
}

.brand-item img:hover {
  filter: grayscale(0) brightness(100%);
  opacity: 1;
}

.brand-link {
  display: block;
  width: 100%;
  height: 100%;
  cursor: pointer;
  transition: all 0.2s ease;
}

.brand-link:hover img {
  filter: grayscale(0) brightness(100%);
  opacity: 1;
  transform: scale(1.05);
}

/* Ajustar responsive para tablets y móviles */
@media (max-width: 991px) {
  .brands-grid {
    gap: 5rem;
    padding: 0 2rem;
  }
  
  .brand-item {
    width: 120px;
    height: 70px;
  }
}

@media (max-width: 768px) {
  .brands-grid {
    gap: 4rem;
    padding: 0 1.5rem;
  }
  
  .brand-item {
    width: 100px;
    height: 60px;
  }
}

@media (max-width: 576px) {
  .brands-grid {
    gap: 3.5rem;
    padding: 0 1rem;
  }
  
  .brand-item {
    width: 80px;
    height: 50px;
  }
}

.brands-wrapper {
  width: 100%;
  overflow: hidden;
  position: relative;
}

.brands-track {
  display: flex;
  width: fit-content;
  animation: slideLogos 30s linear infinite;
}

.brands-track.paused {
  animation-play-state: paused;
}

@keyframes slideLogos {
  0% {
    transform: translateX(0);
  }
  100% {
    transform: translateX(-50%);
  }
}

/* Skeleton Loaders */
.skeleton-brand {
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, #1a1a1a 25%, #2a2a2a 50%, #1a1a1a 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  border-radius: 8px;
}

@keyframes loading {
  to {
    background-position: -200% 0;
  }
}
</style>
